import util from '../../../../../share/util/utils';
import vehicle from '../../../../../share/util/vehicle';
import ApiService from '../../../../../share/util/api/service';
import individualDiagnose from "../../../../../share/util/individualDiagnose";
import dmDidDataConversion from "../../../../../share/util/didDataConversion";
import { WS146_OTHER_CALCULATION_DID } from './dmDataPointConversion';

import dmUtil, { WORKER, WORKER_TASK, WORKER_EVENT } from "../../../dataMonitor/dataMonitorUtil";
import alert from '../../../../../share/alert';

const DIAG_SYSTEM = 'diagSytem.';
const CSV = {
  LINE_BREAK: '\r\n',
  ECU_DECORATION: '################'
};
// Workerの待機間隔
const WORKER_SLEEP_INTERVAL = 500;
// Workerに一度に渡すデータ行数
const WORKER_ROW_LIMIT = 1000;
// CSVのデータが始まる列番号。0列目は時間、1列目はマーカー。
const DATA_COLUMN_NUM = 2;

/**
 * CSV出力を行う
 * @param {object} currentComponent 呼び出し元のコンポーネントのインスタンス
 * @param {object} dataMonitorComponent データモニタのコンポーネントのインスタンス
 * @param {object} projectDetails プロジェクト情報
 * @param {string} measurementId 計測ID
 * @param {string} fileName 出力するCSVのファイル名
 * @param {function} errorDialogCallback エラーダイアログのボタン押下時のコールバック
 */
export async function exportCsv(currentComponent, dataMonitorComponent, projectDetails, measurementId, fileName, errorDialogCallback) {
  // Composable内で保持する値
  let workerInstance = {};
  let workerError = false;
  let hasStopOrder = false;
  let csvData = [];

  try {
    // 子コンポーネントから必要な情報を収集
    const conversionList = dataMonitorComponent.value.conversionList;
    const selectedFuncs = dataMonitorComponent.value.selectedFuncs;
    const baseTime = dataMonitorComponent.value.baseTime;
    const markers = dataMonitorComponent.value.getMarkers();
    const markerTimes = markers.map(marker => Object.values(marker)[0].markerTime).sort();
    const measureData = dataMonitorComponent.value.getMeasureData();

    // CSV用の文字列作成
    const descriptionStr = createCsvDescription();

    const itemIds = selectedFuncs.map(selected => selected.data_monitor_id);
    const itemsBySystem = dmUtil.getItemsBySystem(itemIds);

    const dataBySystem = [];
    // システムごとにCSVのヘッダーとデータ部を作成
    for (const systemName of Object.keys(itemsBySystem)) {
      const headerStr = createCsvHeader(conversionList, itemsBySystem[systemName], systemName, null);

      // 対象システムの選択項目と計測データのみにフィルタ
      const filteredSelected = selectedFuncs.filter(selected => dmUtil.getSystem(selected.data_monitor_id) === systemName);
      const filteredMeasured = {};
      filteredSelected.forEach(item => {
        filteredMeasured[item.data_monitor_id] = measureData[item.data_monitor_id];
      });

      // 計測項目ごとにデータポイント数が異なる可能性があるため、最も多いデータポイント数をCSVの行数にする
      const measurementCount = Object.keys(filteredMeasured).map(did => (filteredMeasured[did] ? filteredMeasured[did].length : 0)).reduce((a, b) => Math.max(a, b), -Infinity);

      let dataStr = '';
      // 大量データをWorkerのメッセージで送受信すると遅くなるため、一定行ごとに処理を行う
      for (let i = 0; i * WORKER_ROW_LIMIT < measurementCount; i++) {
        const startRow = i * WORKER_ROW_LIMIT;
        const partialDataStr = await createCsvData(conversionList, filteredSelected, markerTimes, filteredMeasured, startRow, null, baseTime);
        dataStr = dataStr + partialDataStr;
      }
      dataBySystem.push(headerStr + dataStr);
    }

    // システムごとのデータをまとめて、CSVファイル作成
    util.csvFileExport(descriptionStr + dataBySystem.join(CSV.LINE_BREAK), fileName);
  } finally {
    if (workerInstance instanceof Worker) {
      // Workerの停止と終了
      workerInstance.postMessage({ type: WORKER_TASK.STOP, worker: WORKER.MEASUREMENT_CSV });
      workerInstance.terminate();
      workerInstance = {};
    }
  }

  /**
 * CSVのヘッダー上部の計測時の情報の文字列を作成
 * @returns {string} CSVのヘッダー上部の内容の文字列
 */
  function createCsvDescription() {
    const targetMeasurement = projectDetails.measures[0][ApiService.WSP].find(measure => String(measure.registered_at) === measurementId);

    const dataName = targetMeasurement.name || '';
    const memo = targetMeasurement.note || '';
    const vin = projectDetails.information.vin || '';
    const registrationNumber = (projectDetails.area_name ? projectDetails.area_name + ' ' : '')
      + (projectDetails.category ? projectDetails.category + ' ' : '')
      + (projectDetails.hiragana ? projectDetails.hiragana + ' ' : '')
      + (projectDetails.individual_number ? projectDetails.individual_number + ' ' : '');
    const vehicleName = projectDetails.information.vehicle_name || '';
    // 再生画面ではvehicleInfoのtypeが取得できないため、VINのフォーマットから判定
    const isVin = vehicle.isVinType(vin);
    const vehicleType = (isVin ? projectDetails.information.model_year : projectDetails.information.classification) || '';

    const dataNameDescription = `"${currentComponent.$t('individual.header_data_monitor_data_name')}",${util.escapeForCsv(dataName)}`;
    const memoDescription = `"${currentComponent.$t('individual.label_memo')}",${util.escapeForCsv(memo)}`;
    // vinの種類によって表示する行を変える
    const vinDescription = `"${currentComponent.$t('individual.header_data_monitor_vin')}",${isVin ? util.escapeForCsv(vin) : util.escapeForCsv('')}`;
    const frameNumberDescription = `"${currentComponent.$t('individual.header_data_monitor_frame_number')}",${isVin ? util.escapeForCsv('') : util.escapeForCsv(vin)}`;
    const registrationNumDescription = `"${currentComponent.$t('individual.header_data_monitor_registration_number')}",${util.escapeForCsv(registrationNumber)}`;
    const vehicleNameDescription = `"${currentComponent.$t('individual.header_data_monitor_vehicle_name')}",${util.escapeForCsv(vehicleName)}`;
    const vehicleTypeDescription = `"${currentComponent.$t('individual.header_data_monitor_vehicle_type')}",${util.escapeForCsv(vehicleType)}`;

    return [
      dataNameDescription, memoDescription, vinDescription, frameNumberDescription, registrationNumDescription, vehicleNameDescription, vehicleTypeDescription
    ].join(CSV.LINE_BREAK) + CSV.LINE_BREAK;
  }

  /**
   * CSVのヘッダーの文字列を作成
   * @param {object} conversionList 変換情報のリスト
   * @param {array} itemIds 計測時の選択項目。CSVの列は選択項目の順番で出力する。(e.g. ['101A_1_all', '1012_3_5'])
   * @param {string} systemName システム名
   * @param {string} region リージョン
   * @returns {string} CSVのヘッダーの内容の文字列
   */
  function createCsvHeader(conversionList, itemIds, systemName, region) {
    const findFFDi18nKey = individualDiagnose.getFFDi18nKeyWithRegion(
      currentComponent, region, systemName
    );

    let firstHeader = itemIds.map(
      item => {
        const did = dmUtil.getDid(item.id);
        const name = WS146_OTHER_CALCULATION_DID.includes(did) ?
          util.escapeForCsv(currentComponent.$t(`wsDid${findFFDi18nKey}.${item.id}`)) : util.escapeForCsv(currentComponent.$t(`${findFFDi18nKey}.${item.id}`));
        return name;
      }
    );
    firstHeader.unshift(`"${currentComponent.$t('individual.header_data_monitor_time')}"`, `"${currentComponent.$t('individual.header_data_monitor_mark')}"`);
    firstHeader = firstHeader.join(',');

    let secondHeader = itemIds.map(item => util.escapeForCsv(currentComponent.$t(`didUnit.` + conversionList[dmUtil.setSystem(systemName, item.id)].unit_id)));
    secondHeader.unshift(`"${currentComponent.$t('individual.header_data_monitor_time_unit')}"`, `"${currentComponent.$t('individual.header_data_monitor_mark_unit')}"`);
    secondHeader = secondHeader.join(',');

    const ecuNameDescription = `"${CSV.ECU_DECORATION} ${currentComponent.$t(DIAG_SYSTEM + systemName)} ${CSV.ECU_DECORATION}"`;
    return ecuNameDescription + CSV.LINE_BREAK + firstHeader + CSV.LINE_BREAK + secondHeader + CSV.LINE_BREAK;
  }

  /**
   * CSVのデータ部の文字列を作成
   * @param {object} conversionList 変換情報のリスト
   * @param {array} selectedFuncs 計測時の選択項目。CSVの列は選択項目の順番で出力する。
   * @param {array} markerTimes マーカーの登録時間のリスト
   * @param {object} measureData 計測データ
   * @param {number} startRow 開始行
   * @param {string} region リージョン
   * @param {number} baseTime 基準時刻
   * @returns {string} CSVのデータ部の内容の文字列
   */
  async function createCsvData(conversionList, selectedFuncs, markerTimes, measureData, startRow, region, baseTime) {
    const splittedMeasureData = {};
    for (const did in measureData) {
      splittedMeasureData[did] = measureData[did].slice(startRow, startRow + WORKER_ROW_LIMIT);
    }

    // WorkerでCSVのベースデータ作成
    await startWorker(conversionList, markerTimes, splittedMeasureData, selectedFuncs, baseTime);

    const didList = selectedFuncs.map(item => item.data_monitor_id);

    // CSV全体の行ごとのループ
    const translatedCsvData = csvData.map(row => {
      // 1行の列ごと（計測項目ごと）のループ
      const translatedRow = row.map((cell, index) => {
        if (index < DATA_COLUMN_NUM) {
          // データ部でない列はそのまま使用
          return cell;
        }
        const did = didList[index - DATA_COLUMN_NUM];
        // データ部の列は変換と翻訳処理を行う
        return util.escapeForCsv(String(translate(cell, did, conversionList[did], region)));
      });
      return translatedRow.join(',') + CSV.LINE_BREAK;
    });

    // Workerから返された元のCSV情報はクリア
    csvData.splice(0);

    return translatedCsvData.join('');
  }

  /**
   * 値の翻訳と変換を実施する
   * @param {string} originValue 翻訳・変換対象の値
   * @param {string} did 計測項目
   * @param {object} conversionInfo 変換情報 
   * @param {string|null} region リージョン文字列
   * @returns {string} 翻訳・変換後の値
   */
  function translate(originValue, did, conversionInfo, region) {

    if (originValue && dmUtil.VALUE_TRANSLATE_TYPE.includes(conversionInfo.data_type)) {
      const [systemName, itemId] = dmUtil.splitSystemAndItemId(did);
      let translated = dmUtil.getTranslateValue(currentComponent, itemId, originValue, conversionInfo, region, systemName);
      const id = dmUtil.getDid(dmUtil.getNoSystemItemId(itemId));
      // 2F01-2F08の場合はwsDidから文言を取得する
      if (WS146_OTHER_CALCULATION_DID.includes(id)) {
        const specialKey = `wsDid${systemName}.${itemId}_${originValue}`;
        translated = currentComponent.$te(`${specialKey}`) ? currentComponent.$t(`${specialKey}`) : translated;
      }
      if (conversionInfo.data_type === 'decExStr') {
        return translated || dmDidDataConversion.convertValueDataForHex(originValue, conversionInfo);
      } else {
        return translated;
      }
    } else {
      return originValue;
    }
  }

  /**
   * Web workerの開始処理
   * @param {object} conversionList 変換情報のリスト
   * @param {array} markerTimes マーカーの登録時間のリスト
   * @param {object} measureData 計測データ
   * @param {array} selectedFuncs 計測時の選択項目
   * @param {number} baseTime 基準時刻
   */
  async function startWorker(conversionList, markerTimes, measureData, selectedFuncs, baseTime) {
    // Workerを作成
    if (!(workerInstance instanceof Worker)) {
      // webpackChunkNameを設定した"ws-data-monitor-worker"が、ビルド時のファイル名に使用される
      workerInstance = new Worker(
          /* webpackChunkName: "ws-data-monitor-worker" */ new URL('./DataMonitorWorker.js', import.meta.url)
      );
    }

    const startMessage = {
      worker: WORKER.MEASUREMENT_CSV,
      type: WORKER_TASK.START,
      measureData: JSON.parse(JSON.stringify(measureData)),
      markerTimes: JSON.parse(JSON.stringify(markerTimes)),
      conversionList: JSON.parse(JSON.stringify(conversionList)),
      selectedFuncs: JSON.parse(JSON.stringify(selectedFuncs)),
      baseTime: baseTime
    };

    // Workerに処理開始メッセージを送信
    hasStopOrder = false;
    workerInstance.postMessage(startMessage);

    // Workerエラーフラグのリセット
    workerError = false;

    // Workerからメッセージを受信したときの処理
    workerInstance.onmessage = (event) => {
      csvWorkerListener(event);
    };

    // Workerの処理が完了するまで待機する
    while (!hasStopOrder) {
      // Workerでエラーがあった場合は待機を終了
      if (workerError) {
        break;
      }

      // 一定時間スリープ
      await util.sleep(WORKER_SLEEP_INTERVAL);
    }
  }

  /**
   * csvWorkerからのメッセージ受信処理
   * @param {object} event 
   */
  function csvWorkerListener(event) {
    const data = event.data;

    // エラー受信の場合
    if (data.type === WORKER_EVENT.RUNTIME_ERROR) {
      // Workerでエラーあり
      workerError = true;
      // エラーダイアログ表示
      alert.showError(currentComponent, null, currentComponent.$t('message.common_systemError'), errorDialogCallback);
    }
    // 正常受信(CSVデータ)の場合
    else if (data.type === WORKER_EVENT.CSV_DATA) {
      csvData = csvData.concat(data.csvData);
    }
    // 正常受信(処理完了)の場合
    else if (data.type === WORKER_EVENT.SUCCESS_END) {
      hasStopOrder = true;
    }
  }
}
